/**
 * Configuración de correos de confirmación por empresa (BUSINESS+).
 *
 * Claves en camelCase: el backend recibe camelCase (ParseToSnakeBefore) y responde
 * camelCase (ParseToCamelAfter), así que no hay conversión manual en el front.
 */

/**
 * El tipo sigue incluyendo `flight_confirmation` aunque el panel no lo muestre: el API
 * devuelve una fila por cada tipo y la respuesta tiene que seguir tipando.
 */
export type EmailType =
	| 'hotel_confirmation'
	| 'flight_confirmation'
	| 'approval_confirmation'

/**
 * Cómo sale UNA de las dos versiones del correo.
 *
 * El del viajero y el del solicitante/supervisor son dos correos distintos —distinta
 * plantilla, distinto asunto y ni siquiera el mismo contenido—, así que su sobre y su
 * diseño se configuran aparte. Antes compartían fila, y copiar un buzón en uno copiaba
 * también el otro sin manera de evitarlo.
 */
export interface CompanyEmailVariantSetting {
	recipient: EmailPreviewRecipient
	cc: string[]
	bcc: string[]
	replyTo: string | null
	senderName: string | null
	attachPdf: boolean
	/** Sólo lo usa la confirmación de hotel: los correos de vuelo no traen mapa. */
	showMapLink: boolean
	/** Marca y datos de contacto de Enlacebooking al pie del correo y del PDF. */
	showPlatformBranding: boolean
	emailLayout: EmailLayout
}

export interface CompanyEmailSetting {
	/** null cuando la empresa no ha guardado este tipo: se están viendo los defaults. */
	id: number | null
	companyId: number
	emailType: EmailType

	/**
	 * Lo que decide SI el correo sale. No se separa por variante a propósito: dos
	 * interruptores para la misma decisión dejan al panel sin una respuesta clara.
	 */
	enabled: boolean
	notifyTraveler: boolean
	notifyMember: boolean
	notifySupervisor: boolean

	/**
	 * El viajero ve el importe de su reserva.
	 *
	 * Nace apagado: el correo del viajero nunca llevó dinero, y encenderlo por omisión
	 * pondría un precio frente a cada viajero sin que nadie lo pidiera. El del área de
	 * viajes no depende de esto —ahí el importe sale como siempre—.
	 *
	 * Vive aquí y NO en la variante aunque hable de una versión, igual que en el backend:
	 * es un permiso de la empresa sobre un dato sensible, no una opción de diseño.
	 */
	showTravelerAmount: boolean

	/** Cómo sale cada versión. El API siempre manda las dos, configuradas o no. */
	variants: Record<EmailPreviewRecipient, CompanyEmailVariantSetting>

	updatedAt: string | null
}

/**
 * Dónde van las marcas de la EMPRESA en el correo y en su PDF.
 *
 * El editor arrastra y suelta, pero lo que se guarda es esto: una plantilla y una lista de
 * marcas, cada una con su variante y su RANURA.
 *
 * ⚠️ Son ranuras y no coordenadas porque un correo no es una página web: Outlook renderiza
 * con el motor de Word y no soporta posicionamiento libre. Las ranuras son celdas que ya
 * existen en la plantilla, así que soltar hace snap a una de ellas y lo que se manda es
 * HTML de tabla, que sí sobrevive.
 */
export type EmailTemplate = 'clasica' | 'centrada' | 'minimal'

/**
 * ⚠️ Hubo dos ranuras de pie aquí y ningún blade del backend las pintaba. No es que la
 * marca «no se viera abajo»: como una marca vive en UNA ranura, soltar el logo en el pie lo
 * borraba del correo entero mientras el editor lo seguía enseñando ahí. Vuelven cuando el
 * pie de las dos plantillas tenga sus celdas de verdad.
 */
export type EmailSlot = 'header_left' | 'header_center' | 'header_right'

export type EmailVariant = 'logo' | 'isotipo'

export interface EmailMark {
	variant: EmailVariant
	slot: EmailSlot
}

/**
 * Los bloques del cuerpo, en el orden en que se pintan.
 *
 * Se mueve y se apaga casi todo. Fuera quedan el encabezado y el pie, que son el marco
 * —aunque su contenido sí se configura—, y el saludo, que va fijo: ver FIXED_EMAIL_BLOCKS.
 */
export type EmailBlockId =
	| 'greeting'
	| 'hotel'
	| 'stay'
	| 'money'
	| 'contact'
	| 'reminders'
	| 'cta'

export interface EmailBlockState {
	id: EmailBlockId
	visible: boolean
}

/**
 * Los bloques FIJOS: abren el correo y no se mueven ni se apagan.
 *
 * El saludo es donde el huésped lee su nombre y el número de confirmación, y es lo que
 * alcanza a verse en la previa del celular sin abrir el correo.
 *
 * Esto es sólo la mitad de la regla: quien manda es el servidor (EmailLayout::FIXED_BLOCKS
 * en BookingV2), que reacomoda y prende estos bloques al leer lo guardado. Aquí viven para
 * que el editor no ofrezca algo que el correo va a ignorar.
 */
export const FIXED_EMAIL_BLOCKS: EmailBlockId[] = ['greeting']

export interface EmailBlockOption {
	value: EmailBlockId
	label: string
	description: string
}

export const EMAIL_BLOCKS: EmailBlockOption[] = [
	{
		value: 'greeting',
		label: 'Saludo y confirmación',
		description: 'El nombre del viajero y el número de confirmación.',
	},
	{
		value: 'hotel',
		label: 'Hospedaje',
		description: 'Nombre, dirección, fachada y mapa.',
	},
	{
		value: 'stay',
		label: 'Estancia',
		description: 'Check-in, check-out, noches y habitación.',
	},
	{
		value: 'money',
		label: 'Importe',
		description:
			'Total e impuestos. En la del viajero sólo si tu empresa lo activa.',
	},
	{
		value: 'contact',
		label: 'Encargado de viajes',
		description: 'Quién gestionó la reserva y cómo contactarlo.',
	},
	{
		value: 'reminders',
		label: 'Recuerda',
		description: 'Los avisos para el viajero al llegar.',
	},
	{
		value: 'cta',
		label: 'Botón',
		description: 'El enlace a la reservación en el sistema.',
	},
]

/**
 * Los bloques que NO existen en cada versión del correo.
 *
 * ⚠️ Es un espejo de `EmailLayout::BLOCKS_NOT_IN` en el backend, que es quien MANDA: el
 * recorrido del blade cruza lo guardado contra su propio catálogo, así que una lista mal
 * puesta aquí ya no puede colar un bloque en el correo equivocado — sólo pinta un
 * interruptor que no hace nada. Esta copia existe para que el panel no ofrezca esos
 * interruptores; que el catálogo venga del API está pendiente.
 *
 * Se desincronizó una vez y costó caro: en «Solicitante y supervisor» los siete bloques
 * salían operables, y ese correo ni siquiera recorría bloques. Apagar «Importe» guardaba
 * `visible:false` y el total se seguía mandando.
 *
 * ⚠️ «Importe» SALIÓ de la lista del viajero: dejó de ser imposible y pasó a ser opcional.
 * Su interruptor en esa versión no escribe en el bloque —ése lo comparten las dos— sino en
 * `showTravelerAmount`. Ver `EmailLayout::BLOCKS_OPT_IN` del backend, que es quien manda.
 */
export const BLOCKS_NOT_IN: Record<EmailPreviewRecipient, EmailBlockId[]> = {
	traveler: [],
	manager: ['reminders'],
}

/** Los bloques que no aplican a la versión que se está editando. */
export function blocksNotIn(recipient: EmailPreviewRecipient): EmailBlockId[] {
	return BLOCKS_NOT_IN[recipient] ?? []
}

export interface EmailLayout {
	template: EmailTemplate
	marks: EmailMark[]
	blocks: EmailBlockState[]
}

export interface EmailTemplateOption {
	value: EmailTemplate
	label: string
	description: string
}

export const EMAIL_TEMPLATES: EmailTemplateOption[] = [
	{
		value: 'clasica',
		label: 'Clásica',
		description:
			'Banda azul con la marca a la izquierda y el estado al centro.',
	},
	{
		value: 'centrada',
		label: 'Centrada',
		description: 'Banda azul con la marca al centro y el estado debajo.',
	},
	{
		value: 'minimal',
		label: 'Minimal',
		description: 'Sin banda de color, para que nada compita con tu marca.',
	},
]

export interface EmailSlotOption {
	value: EmailSlot
	label: string
	zone: 'header'
}

/** Las ranuras, en el orden en que aparecen en el correo. */
export const EMAIL_SLOTS: EmailSlotOption[] = [
	{ value: 'header_left', label: 'Izquierda', zone: 'header' },
	{ value: 'header_center', label: 'Centro', zone: 'header' },
	{ value: 'header_right', label: 'Derecha', zone: 'header' },
]

export const EMAIL_VARIANTS: { value: EmailVariant; label: string }[] = [
	{ value: 'logo', label: 'Logo' },
	{ value: 'isotipo', label: 'Isotipo' },
]

export const DEFAULT_EMAIL_LAYOUT: EmailLayout = {
	template: 'clasica',
	marks: [{ variant: 'logo', slot: 'header_left' }],
	blocks: EMAIL_BLOCKS.map((b) => ({ id: b.value, visible: true })),
}

/** Una versión sin configurar, para armar el estado del formulario sin inventar valores. */
export function defaultEmailVariant(
	recipient: EmailPreviewRecipient
): CompanyEmailVariantSetting {
	return {
		recipient,
		cc: [],
		bcc: [],
		replyTo: null,
		senderName: null,
		attachPdf: true,
		showMapLink: true,
		showPlatformBranding: true,
		emailLayout: structuredClone(DEFAULT_EMAIL_LAYOUT),
	}
}

export type CompanyEmailSettingUpdateBody = Omit<
	CompanyEmailSetting,
	'id' | 'companyId' | 'updatedAt'
>

export interface EmailTypeOption {
	value: EmailType
	label: string
	icon: string
	description: string
}

/**
 * Las pestañas que el panel muestra.
 *
 * La confirmación de VUELO no está: el canal sigue en validación y en el backend nace
 * apagada (`CompanyEmailSetting::DEFAULT_OVERRIDES`), así que enseñar la pestaña sería
 * ofrecer un interruptor que no manda nada. Volver a mostrarla es devolver aquí su entrada
 * —el tipo, el resolver y el endpoint nunca dejaron de soportarla.
 */
export const EMAIL_TYPE_OPTIONS: EmailTypeOption[] = [
	{
		value: 'hotel_confirmation',
		label: 'Confirmación de hotel',
		icon: 'hotel',
		description: 'Se envía cuando una reserva de hotel queda confirmada.',
	},
	{
		value: 'approval_confirmation',
		label: 'Reserva aprobada',
		icon: 'task_alt',
		description:
			'Se envía cuando una reserva que requería aprobación queda confirmada.',
	},
]

/** Tope que también valida el backend: son copias fijas, no una lista de difusión. */
export const MAX_COPY_ADDRESSES = 10

/**
 * Las dos variantes del correo. Cada tipo tiene un blade por variante: el viajero recibe
 * su propia versión y solicitante y supervisor comparten la de "manager".
 */
export type EmailPreviewRecipient = 'traveler' | 'manager'

export interface EmailPreviewOption {
	value: EmailPreviewRecipient
	label: string
}

export const EMAIL_PREVIEW_OPTIONS: EmailPreviewOption[] = [
	{ value: 'traveler', label: 'Viajero' },
	{ value: 'manager', label: 'Solicitante y supervisor' },
]

/**
 * Cuerpo del correo de prueba. Lleva la variante que se está previsualizando para que la
 * prueba llegue igual a lo que el usuario tiene en pantalla.
 */
export interface SendTestBody {
	emailType: EmailType
	recipient: EmailPreviewRecipient
}
